import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Tab,
  Box,
  Stack,
  Badge,
  Drawer,
  Button,
  SvgIcon,
  Tooltip,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import type { IconButtonProps } from '@mui/material/IconButton';
import { m } from 'framer-motion';
import { decodeJWT } from 'aws-amplify/auth';

import { NotificationItem } from './notification-item';
import type { NotificationItemProps } from './notification-item';

import { useTranslate } from 'src/locales';
import { useBoolean } from 'src/hooks/use-boolean';
import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { varHover } from 'src/components/animate';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomTabs } from 'src/components/custom-tabs';
import { toast } from 'src/components/snackbar';
import EncryptUtils from 'src/utils/EncryptUtils';
import { notificationDataService } from 'src/services/service-handlers/PrivateService';
import { useAuthContext } from 'src/context/AuthContext';
import { _mock } from 'src/_mock';
import { useScrollNotification } from 'src/sections/chat/hooks/use-notifications-scroll';
import AuthUtils from 'src/utils/AuthUtils';

// Map API notification title/category strings to translation keys (notifications.msg* / notifications.cat*)
const NOTIFICATION_TITLE_KEYS: Record<string, string> = {
  // Full application access (entities)
  'granted you access to the <strong>Full Application</strong>': 'msgGrantedFullApplication',
  'revoked your access to the <strong>Full Application</strong>': 'msgRevokedFullApplication',
  // Waitlist (pre-application)
  'added your application to the <strong>Affordable Waitlist</strong>': 'msgAddToAffordableWaitlist',
  'added your application to the <strong>Attainable Waitlist</strong>': 'msgAddToAttainableWaitlist',
  'added your application to both <strong>Affordable</strong> and <strong>Attainable Waitlists</strong>':
    'msgAddToBothWaitlists',
  'removed your application from the <strong>Affordable Waitlist</strong>': 'msgRemoveFromAffordable',
  'removed your application from the <strong>Attainable Waitlist</strong>': 'msgRemoveFromAttainable',
  'moved your application to the <strong>Attainable Waitlist</strong>': 'msgMoveToAttainable',
  'moved your application to the <strong>Affordable Waitlist</strong>': 'msgMoveToAffordable',
  'removed your application from <strong>all waitlists</strong>': 'msgRemoveFromAllWaitlists',
  // Pre-application status
  'removed your <strong>application</strong>': 'msgRemovedApplication',
  'returned your application for <strong>more information</strong>': 'msgReturnedForMoreInfo',
  'submitted a <strong>new pre-application</strong>': 'msgPreAppSubmitted',
  'updated your <strong>pre-application</strong>': 'msgPreAppUpdated',
  'rescinded your <strong>pre-application</strong>': 'msgPreAppRescinded',
  'moved your application to <strong>Pending Review</strong>': 'msgMovedToPendingReview',
  'submitted a new pre-application': 'msgPreAppSubmittedPlain',
  'updated their pre-application': 'msgPreAppUpdatedTheir',
  'rescinded their pre-application': 'msgPreAppRescindedTheir',
  // Full application
  'submitted a <strong>new full application</strong>': 'msgFullAppSubmitted',
  'updated your <strong>full application</strong>': 'msgFullAppUpdated',
  'rescinded your <strong>full application</strong>': 'msgFullAppRescinded',
  'removed your <strong>full application</strong>': 'msgFullAppRemoved',
  'returned your <strong>full application</strong> for more information': 'msgFullAppReturned',
  'accepted your <strong>full application</strong>': 'msgFullAppAccepted',
  'marked you as the <strong>property owner</strong> for your full application': 'msgFullAppMarkedOwner',
  'submitted a new full-application': 'msgFullAppSubmittedPlain',
  'updated their full-application': 'msgFullAppUpdatedTheir',
  'rescinded their full-application': 'msgFullAppRescindedTheir',
  // Compliance review
  'submitted compliance review information': 'msgComplianceSubmitted',
  'updated compliance review details': 'msgComplianceUpdated',
  'submitted a <strong>new compliance review</strong>': 'msgComplianceSubmittedNew',
  'updated your <strong>compliance review</strong>': 'msgComplianceUpdatedYour',
  'rescinded your <strong>compliance review</strong>': 'msgComplianceRescinded',
  'accepted your <strong>compliance review</strong>': 'msgComplianceAccepted',
  'returned your <strong>compliance review</strong> for more information': 'msgComplianceReturned',
  'marked your <strong>compliance review</strong> as non-compliant': 'msgComplianceNonCompliant',
};
// Match property status notifications by substring (API may replace ###Location with actual address)
function getPropertyStatusTitleKey(title: string): string | null {
  if (!title || !title.includes('has marked the property')) return null;
  if (title.includes('as <strong>Available</strong>')) return 'msgPropertyMarkedAvailable';
  if (title.includes('as <strong>Unavailable</strong>')) return 'msgPropertyMarkedUnavailable';
  if (title.includes('as <strong>Sold</strong>')) return 'msgPropertyMarkedSold';
  if (title.includes('as <strong>Rented</strong>')) return 'msgPropertyMarkedRented';
  return 'msgPropertyStatus';
}
const NOTIFICATION_CATEGORY_KEYS: Record<string, string> = {
  'Full Application Access': 'catFullApplicationAccess',
  'Waitlist Assignment': 'catWaitlistAssignment',
  'Waitlist Update': 'catWaitlistUpdate',
  'Pre-Application Status': 'catPreApplicationStatus',
  'Pre-Application Submission': 'catPreApplicationSubmission',
  'Pre-Application Update': 'catPreApplicationUpdate',
  'Pre-Application Rescind': 'catPreApplicationRescind',
  General: 'catGeneral',
  'Full Application Submission': 'catFullApplicationSubmission',
  'Full Application Update': 'catFullApplicationUpdate',
  'Full Application Rescind': 'catFullApplicationRescind',
  'Full Application Status': 'catFullApplicationStatus',
  'Full-Application Submission': 'catFullApplicationSubmission',
  'Full-Application Update': 'catFullApplicationUpdate',
  'Full-Application Rescind': 'catFullApplicationRescind',
  'Compliance Review Submission': 'catComplianceReviewSubmission',
  'Compliance Review Update': 'catComplianceReviewUpdate',
  'Compliance Review Rescind': 'catComplianceReviewRescind',
  'Compliance Review Status': 'catComplianceReviewStatus',
  'Property Status': 'catPropertyStatus',
};

// ----------------------------------------------------------------------

export type NotificationsDrawerProps = IconButtonProps & {
  item?: any;
  data?: NotificationItemProps[];
};

export function NotificationsDrawer({ item, data = [], sx, ...other }: NotificationsDrawerProps) {
  const { t } = useTranslate('common');
  const tRef = useRef(t);
  tRef.current = t;
  const drawer = useBoolean();
  const { authState } = useAuthContext();
  const token = AuthUtils.getAuthToken();
  // Guard against a missing/stale token during navigation transitions —
  // decodeJWT throws "Invalid token" on empty/malformed input.
  let decoded: any = null;
  try {
    if (token) decoded = decodeJWT(token);
  } catch {
    decoded = null;
  }

  const [currentTab, setCurrentTab] = useState('all');
  const [notifications, setNotifications] = useState<NotificationItemProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [scrollPaginationEnabled, setScrollPaginationEnabled] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const pageSize = 10;

  const { isAdmin } = authState;
  const role = isAdmin ? 'admin' : 'user';
  const module_name = 'notifications';
  const personId: any = isAdmin ? null : (decoded?.payload?.user as any)?.PersonId;

  const isInitialLoad = notifications.length === 0 && loading;

  const totalUnRead = notifications.filter((notification) => notification.isUnRead === true).length;
  const totalRead = notifications.length - totalUnRead;

  const { scrollRef } = useScrollNotification(scrollPaginationEnabled, loading, hasMore, () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadNotifications(nextPage); // auto-load next page on scroll
  });

  const loadNotifications = useCallback(
    async (pageNumber: number = 1, isInitial = false) => {
      try {
        setLoading(true);
        const offset = (pageNumber - 1) * pageSize;

        const payload: any = {
          sql_data: item?.sql_data,
          person_id: personId,
          page_size: pageSize,
          offset,
        };

        const encrypted = EncryptUtils.encryptedData(payload);
        const response = await notificationDataService(role, module_name, 'get', { tk: encrypted });
        const rows = response?.data || [];

        const formatNotification = (row: any, index: number): NotificationItemProps => {
          const avatarIndex = (index + offset) % 25;
          const rawTitle = row.Title || '';
          const rawCategory = row.Category || 'General';
          const titleKey =
            NOTIFICATION_TITLE_KEYS[rawTitle] ?? getPropertyStatusTitleKey(rawTitle);
          const categoryKey = NOTIFICATION_CATEGORY_KEYS[rawCategory];
          return {
            id: String(row.NotificationID),
            name: isAdmin ? row.UserFullName : tRef.current('notifications.supportTeam'),
            title: titleKey ? tRef.current(`notifications.${titleKey}`) : rawTitle,
            category: categoryKey ? tRef.current(`notifications.${categoryKey}`) : rawCategory,
            isUnRead: isAdmin ? row.AdminMarkAsRead === false : row.UserMarkAsRead === false,
            avatarUrl: _mock.image.avatar(avatarIndex),
            type: row.IsTriggeredByUser ? 'user' : 'admin',
            createdAt: row.CreatedDate || null,
            userPersonId: row.UserPersonId || null,
          };
        };

        const formatted = Array.isArray(rows)
          ? rows.map(formatNotification)
          : rows.length === 0
            ? []
            : [formatNotification(rows, 0)];

        setNotifications((prev) => (isInitial ? formatted : [...prev, ...formatted]));

        setHasMore(formatted.length === pageSize); // disable scroll if fewer than 10 returned
      } catch (err) {
        console.error('Failed to load notifications:', err);
        toast.error(tRef.current('notifications.failedToLoad'));
      } finally {
        setLoading(false);
      }
    },
    [role, module_name, item?.sql_data, pageSize, personId, isAdmin]
  );

  useEffect(() => {
    setScrollPaginationEnabled(false);
    setPage(1);
    setHasMore(true);
    setNotifications([]);
    loadNotifications(1, true);
  }, [drawer.value, loadNotifications]);

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const handleMarkAsRead = async (notificationId: string, userPersonId?: number) => {
    try {
      const sqlConfig = isAdmin
        ? item?.admin_mark_as_read_sql_data
        : item?.user_mark_as_read_sql_data;

      if (!sqlConfig) return;

      const payload: any = {
        sql_data: sqlConfig,
        notification_id: Number(notificationId),
        ...(isAdmin
          ? {
              admin_person_id: decoded?.payload?.PersonId,
              user_person_id: userPersonId || null, // Needed for admin
            }
          : {
              user_person_id: personId,
            }),
      };

      const encrypted = EncryptUtils.encryptedData(payload);
      await notificationDataService(role, module_name, 'mark-as-read', { tk: encrypted });
    } catch (err) {
      console.error('Failed to mark as read:', err);
      toast.error(t('notifications.failedToMarkAsRead'));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const sqlConfig = isAdmin
        ? item?.admin_mark_as_read_sql_data
        : item?.user_mark_as_read_sql_data;

      if (!sqlConfig) return;

      const payload: any = {
        sql_data: sqlConfig,
        ...(isAdmin
          ? {
              admin_person_id: decoded?.payload?.PersonId,
              user_person_id: null,
              notification_id: null,
            }
          : {
              user_person_id: personId,
            }),
      };

      const encrypted = EncryptUtils.encryptedData(payload);
      await notificationDataService(role, module_name, 'mark-all-as-read', { tk: encrypted });

      // Update local UI state
      handleMarkAllAsReadUI();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      toast.error(t('notifications.failedToMarkAllAsRead'));
    }
  };

  const handleMarkAllAsReadUI = () => {
    setNotifications((prev) =>
      prev.map((notification) => ({
        ...notification,
        isUnRead: false,
      }))
    );
  };

  const TABS = [
    { value: 'all', label: t('notifications.tabAll'), count: notifications.length },
    { value: 'read', label: t('notifications.tabRead'), count: totalRead },
    { value: 'unread', label: t('notifications.tabUnread'), count: totalUnRead },
  ];

  const filteredNotifications = notifications.filter((notification) => {
    if (currentTab === 'unread') return notification.isUnRead;
    if (currentTab === 'read') return !notification.isUnRead;
    return true; // 'all'
  });

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        {t('notifications.title')}
      </Typography>

      {!!totalUnRead && (
        <Tooltip title={t('notifications.markAllAsRead')}>
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      <IconButton onClick={drawer.onFalse} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
        <Iconify icon="mingcute:close-line" />
      </IconButton>
    </Stack>
  );

  const renderTabs = (
    <CustomTabs variant="fullWidth" value={currentTab} onChange={handleChangeTab}>
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'unread' && 'info') ||
                (tab.value === 'read' && 'success') ||
                'default'
              }
            >
              {tab.count}
            </Label>
          }
        />
      ))}
    </CustomTabs>
  );

  const renderList = (
    <Scrollbar ref={scrollRef}>
      <Box component="ul" sx={{ minHeight: 320, position: 'relative' }}>
        {isInitialLoad ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: 320 }}>
            <CircularProgress />
          </Stack>
        ) : filteredNotifications.length > 0 ? (
          <>
            {filteredNotifications.map((notification) => (
              <Box component="li" key={notification.id} sx={{ display: 'flex' }}>
                <NotificationItem
                  notification={notification}
                  onClick={() => {
                    if (notification.isUnRead) {
                      handleMarkAsRead(
                        notification.id,
                        isAdmin ? notification.userPersonId : undefined
                      ); // pass userPersonId if admin
                      setNotifications((prev) =>
                        prev.map((notif) =>
                          notif.id === notification.id ? { ...notif, isUnRead: false } : notif
                        )
                      );
                    }
                  }}
                />
              </Box>
            ))}

            {scrollPaginationEnabled && loading && (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 2 }}>
                <CircularProgress size={20} />
              </Stack>
            )}
          </>
        ) : (
          <Box
            sx={{
              px: 2.5,
              py: 5,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="subtitle2">
              {currentTab === 'unread'
                ? t('notifications.noUnread')
                : currentTab === 'read'
                  ? t('notifications.noRead')
                  : t('notifications.noNotifications')}
            </Typography>
          </Box>
        )}
      </Box>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={drawer.onTrue}
        sx={sx}
        {...other}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <SvgIcon>
            <path
              fill="currentColor"
              d="M18.75 9v.704c0 .845.24 1.671.692 2.374l1.108 1.723c1.011 1.574.239 3.713-1.52 4.21a25.794 25.794 0 0 1-14.06 0c-1.759-.497-2.531-2.636-1.52-4.21l1.108-1.723a4.393 4.393 0 0 0 .693-2.374V9c0-3.866 3.022-7 6.749-7s6.75 3.134 6.75 7"
              opacity="0.5"
            />
            <path
              fill="currentColor"
              d="M12.75 6a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0zM7.243 18.545a5.002 5.002 0 0 0 9.513 0c-3.145.59-6.367.59-9.513 0"
            />
          </SvgIcon>
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 1, maxWidth: 420 } }}
      >
        {renderHead}

        {renderTabs}

        {renderList}

        <Button
          fullWidth
          size="large"
          onClick={() => {
            if (!scrollPaginationEnabled) {
              setScrollPaginationEnabled(true);
              setPage(2);
              loadNotifications(2);
            } else {
              // Reset to page 1
              setScrollPaginationEnabled(false);
              setPage(1);
              setHasMore(true);
              loadNotifications(1, true);
            }
          }}
          disabled={loading}
        >
          {loading ? t('notifications.loading') : scrollPaginationEnabled ? t('notifications.viewLess') : t('notifications.viewAll')}
        </Button>
      </Drawer>
    </>
  );
}
